/* eslint-disable camelcase -- backend wire-format field names */
/**
 * First-touch attribution: records how a visitor first reached Livewello (UTMs,
 * affiliate ref, landing path, referring site) so signups and kit purchases can
 * be traced to a channel. Stored once in localStorage and never overwritten.
 *
 * Privacy: first-party, no click IDs (fbclid, gclid, ...), no query string, no
 * tokens or usernames from the path. Mirrors the server-side sanitiser; bad
 * values are dropped rather than rejected so attribution never blocks a signup.
 */

const STORAGE_KEY = 'lw_first_touch'

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
const UTM_PATTERN = /^[A-Za-z0-9 ._+:|/-]{1,100}$/
const REF_PATTERN = /^[A-Za-z0-9_-]{1,100}$/
const SLUG_PATTERN = /^[a-z0-9-]{1,40}$/

// Only these public sections have meaningful second segments worth keeping
// (everything else may carry tokens, ids or usernames).
const TWO_SEGMENT_SECTIONS = new Set([
  '/coaching',
  '/partners',
  '/how-to-use',
  '/spotlight',
  '/practitioner-spotlight',
  '/dna-kit',
  '/goals-partners',
])
// ...except these exact pages, whose query or segment carries a token.
const FIRST_SEGMENT_ONLY = new Set(['/dna-kit/claim'])

// Signed-in app routes (kept in step with isMarketingRoute in marketingGoogleAds.ts).
const SIGNED_IN_PREFIXES = [
  '/account',
  '/dashboard',
  '/genechat',
  '/wellness',
  '/health-records',
  '/billing',
  '/pro-dashboard',
  '/ap/',
  '/chat',
  '/messages',
  '/documents',
  '/support',
  '/partnerships',
  '/profiles',
  '/snp-library',
  '/genetic-reports',
  '/discovery',
  '/admin',
  '/goals',
  '/settings',
]

export interface FirstTouch {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  ref?: string
  landing_path: string
  referring_domain?: string
  captured_at: string
}

export interface LocationLike {
  pathname: string
  search: string
  hostname: string
}

// Public pages that share a prefix with a signed-in route above (e.g. /goals vs /goals-partners).
const PUBLIC_EXCEPTIONS = ['/account/delete', '/goals-partners']

function isSignedInRoute(pathname: string): boolean {
  if (PUBLIC_EXCEPTIONS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return false
  return SIGNED_IN_PREFIXES.some((p) => pathname.startsWith(p))
}

function landingPath(pathname: string): string {
  const [first, second] = pathname.split('/').filter(Boolean)
  if (!first || !SLUG_PATTERN.test(first.toLowerCase())) return '/'
  const firstPath = `/${first}`
  if (!second || !SLUG_PATTERN.test(second)) return firstPath
  const twoSegments = `${firstPath}/${second}`
  return TWO_SEGMENT_SECTIONS.has(firstPath) && !FIRST_SEGMENT_ONLY.has(twoSegments)
    ? twoSegments
    : firstPath
}

function referringDomain(referrer: string, ownHostname: string): string | undefined {
  if (!referrer) return undefined
  try {
    const host = new URL(referrer).hostname
    return host && host !== ownHostname ? host : undefined
  } catch {
    return undefined
  }
}

/** Store the first-touch record once. Returns silently if already stored or on a signed-in route. */
export function captureFirstTouch(
  location: LocationLike,
  referrer: string,
  now: number = Date.now()
): void {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return
    if (isSignedInRoute(location.pathname)) return

    const params = new URLSearchParams(location.search)
    const touch: FirstTouch = {
      landing_path: landingPath(location.pathname),
      captured_at: new Date(now).toISOString(),
    }
    for (const key of UTM_KEYS) {
      const value = params.get(key)
      if (value && UTM_PATTERN.test(value)) touch[key] = value
    }
    const ref = params.get('ref')
    if (ref && REF_PATTERN.test(ref)) touch.ref = ref
    const domain = referringDomain(referrer, location.hostname)
    if (domain) touch.referring_domain = domain

    localStorage.setItem(STORAGE_KEY, JSON.stringify(touch))
  } catch {
    // Storage unavailable (private mode, blocked) — attribution is best effort.
  }
}

export function getFirstTouch(): FirstTouch | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as FirstTouch) : undefined
  } catch {
    return undefined
  }
}
